/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import { EditOutlined, LayoutOutlined } from '@ant-design/icons';
import { Button, theme as antTheme } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import {
  Component,
  ComponentInstance,
  ComponentLayout,
  DataGridItem,
} from '@totem/components/customDashboard/home/lib/types';
import TitleEditModal from '@totem/components/customDashboard/home/TitleEditModal';
import WidgetPaletteDrawer from '@totem/components/customDashboard/home/WidgetPaletteDrawer';
import Widget from '@totem/components/customDashboard/home/widgets/Widget';
import WidgetInstance from '@totem/components/customDashboard/home/widgets/WidgetInstance';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  WIDGET_LAYOUT_ENDPOINT,
  WIDGET_PALETTE_ENDPOINT,
} from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import ContentLayout from '../../ui_components/page/ContentLayout';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const styles = {
  editIcon: {
    marginRight: '1rem',
  },
  infoWidgetPanel: {
    overflow: 'hidden',
  },
  widgetPanel: {
    overflow: 'hidden',
    height: '100%',
    borderBottom: '1px solid #ddd',
  },
  primaryContentPanePage: {
    display: 'flex',
    flex: 1,
    flowFlow: 'column nowrap',
    border: '0 1px 0 0 ',
    minHeight: '240px',
    width: '100%',
  },
};

const defaultProps = {
  className: 'layout',
  breakpoints: { lg: 2560, md: 1600, sm: 1024, xs: 0 },
  cols: { lg: 24, md: 12, sm: 6, xs: 3 },
  rowHeight: 25,
  margin: [10, 20] as [number, number],
};

const Home = () => {
  const { token } = antTheme.useToken();
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const adaptiveLayout = true;
  const ResponsiveReactGridLayout = useMemo(
    () => WidthProvider(Responsive),
    [],
  );
  const [currentWidth, setCurrentWidth] = useState<number>(window.innerWidth);
  const GetLayoutSize = (width: number) => {
    if (width >= defaultProps.breakpoints.lg) {
      return 'lg';
    }
    if (width >= defaultProps.breakpoints.md) {
      return 'md';
    }
    if (width >= defaultProps.breakpoints.sm) {
      return 'sm';
    }
    return 'xs';
  };

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [layout, setLayout] = useState<ComponentLayout>({
    id: '64f727cbc88cd4dfc96f8daf',
    pageName: 'Home',
    pageNumber: 1,
    organizationId: '5f2c6593a679f910caaa31b9',
    userId: '61dda0b7028dcf440ac0d285',
    title: 'My Alerts Page',
    components: [],
  });
  const [layoutSize, setLayoutSize] = useState<string>(
    GetLayoutSize(currentWidth),
  );

  const [componentPallet, setComponentPallet] = useState<Component[]>([]);
  const [showTitleEdit, setShowTitleEdit] = useState<boolean>(false);
  const [isSending, setIsSending] = useState(false);
  const [showWidgets, setShowWidgets] = useState(false);
  const [droppingComponent, setDroppingComponent] = useState<Component | null>(
    null,
  );

  useEffect(() => {
    fetch(`${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: ComponentLayout) => {
        setLayout(result);

        window.dispatchEvent(new Event('resize'));
      });
  }, [pageNumber, userProfile.organization.id, getApiToken]);

  useEffect(() => {
    fetch(`${WIDGET_PALETTE_ENDPOINT}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: Component[]) => {
        if (Array.isArray(result)) {
          setComponentPallet(result);
        }
      });
  }, [userProfile.organization.id, getApiToken]);

  const saveLayoutGeometry = useCallback(
    async (pageNum, geometry, size) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      fetch(`${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}/${size}`, {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(geometry),
      })
        .then((res) => res.json())
        .then((result: ComponentLayout) => {
          setIsSending(false);

          setLayout(result);
          //window.dispatchEvent(new Event('resize'));
        });
    },
    [getApiToken],
  );

  const saveLayoutTitle = useCallback(
    async (pageNum, title) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      fetch(`${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}/title`, {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify({ title }),
      }).then((res) => {
        setIsSending(false);

        CheckResponseShowError(res);
      });
    },
    [getApiToken],
  );

  const addComponentInstance = useCallback(
    async (pageNum: number, newComponentInstance: ComponentInstance) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      fetch(`${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(newComponentInstance),
      })
        .then((res) => {
          setIsSending(false);

          CheckResponseShowError(res);
          return res;
        })
        .then((res) => res.json())
        .then((result: ComponentLayout) => {
          setLayout(result);
        });
    },
    [],
  );

  const removeComponentInstance = useCallback(async (pageNum, instanceId) => {
    if (isSending) {
      return;
    }
    setIsSending(true);

    fetch(`${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}/${instanceId}`, {
      method: 'DELETE',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    }).then((res) => {
      setIsSending(false);

      CheckResponseShowError(res);
    });
  }, []);

  const saveWidgetTitle = useCallback(
    async (componentInstanceId: string, newTitle: string) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      fetch(
        `${WIDGET_LAYOUT_ENDPOINT}/Home/${pageNumber}/${componentInstanceId}/title`,
        {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify({ title: newTitle }),
        },
      ).then((res) => {
        setIsSending(false);

        CheckResponseShowError(res);
      });
    },
    [getApiToken, isSending, pageNumber],
  );

  const handleRenameItem = (componentInstanceId: string, newTitle: string) => {
    setLayout({
      ...layout,
      components: layout.components.map((comp) =>
        comp.componentInstanceId === componentInstanceId
          ? { ...comp, title: newTitle }
          : comp,
      ),
    });

    saveWidgetTitle(componentInstanceId, newTitle);
  };

  const handleRemoveItem = (id: string) => {
    setLayout({
      ...layout,
      components: layout.components.filter(
        (comp) => comp.componentInstanceId !== id,
      ),
    });

    removeComponentInstance(pageNumber, id);
  };

  const handleEditTitle = () => {
    setShowTitleEdit(true);
  };

  const handleTitleChange = (newTitle: string) => {
    setLayout({
      ...layout,
      title: newTitle,
    });

    saveLayoutTitle(pageNumber, newTitle);

    setShowTitleEdit(false);
  };

  const handleSave = (data: DataGridItem[]) => {
    saveLayoutGeometry(pageNumber, data, layoutSize);
  };

  const handleBreakpointChange = (data: any) => {
    setLayoutSize(data);
  };

  const handleWidthChange = (containerWidth: number) => {
    setCurrentWidth(containerWidth);
  };

  const buildComponentInstance = (
    componentData: Component,
    dropPosition?: { x: number; y: number; w: number; h: number },
  ): ComponentInstance => {
    const newComponentInstanceId = uuidv4();
    const pos = dropPosition ?? {
      x: componentData.geometry.x,
      y: componentData.geometry.y,
      // eslint-disable-next-line id-length
      w: componentData.geometry.w,
      // eslint-disable-next-line id-length
      h: componentData.geometry.h,
    };

    return {
      title: componentData.title,
      style: componentData.style,
      componentId: componentData.componentId,
      componentInstanceId: newComponentInstanceId,
      geometry: {
        ...componentData.geometry,
        // eslint-disable-next-line id-length
        i: newComponentInstanceId,
        x: pos.x,
        y: pos.y,
        // eslint-disable-next-line id-length
        w: pos.w,
        // eslint-disable-next-line id-length
        h: pos.h,
      },
      positionGeometry: [
        {
          size: layoutSize,
          x: pos.x,
          y: pos.y,
          // eslint-disable-next-line id-length
          w: pos.w,
          // eslint-disable-next-line id-length
          h: pos.h,
        },
      ],
    };
  };

  const handleAddComponent = (componentData: Component) => {
    addComponentInstance(pageNumber, buildComponentInstance(componentData));

    setShowWidgets(false);
  };

  const handleDrop = (
    _droppedLayout: DataGridItem[],
    item: DataGridItem,
    _evt: Event,
  ) => {
    if (!droppingComponent) {
      return;
    }

    addComponentInstance(
      pageNumber,
      buildComponentInstance(droppingComponent, {
        x: item.x,
        y: item.y,
        // eslint-disable-next-line id-length
        w: item.w,
        // eslint-disable-next-line id-length
        h: item.h,
      }),
    );

    setDroppingComponent(null);

    setShowWidgets(false);
  };

  const GetGeometry = (componentInstance: ComponentInstance, size: string) => {
    const geometry = { ...componentInstance.geometry };
    if (
      adaptiveLayout &&
      typeof componentInstance.positionGeometry !== 'undefined' &&
      componentInstance.positionGeometry !== null
    ) {
      for (
        let idx = 0;
        idx < componentInstance.positionGeometry.length;
        idx++
      ) {
        if (componentInstance.positionGeometry[idx].size === size) {
          geometry.x = componentInstance.positionGeometry[idx].x;

          geometry.y = componentInstance.positionGeometry[idx].y;

          // eslint-disable-next-line id-length
          geometry.w = componentInstance.positionGeometry[idx].w;

          // eslint-disable-next-line id-length
          geometry.h = componentInstance.positionGeometry[idx].h;
        }
      }
    }

    if (geometry.minW !== null && geometry.w < geometry.minW) {
      // eslint-disable-next-line id-length
      geometry.w = geometry.minW;
    }
    if (geometry.maxW !== null && geometry.w > geometry.maxW) {
      // eslint-disable-next-line id-length
      geometry.w = geometry.maxW;
    }
    if (geometry.minH !== null && geometry.h < geometry.minH) {
      // eslint-disable-next-line id-length
      geometry.h = geometry.minH;
    }
    if (geometry.maxH !== null && geometry.h > geometry.maxH) {
      // eslint-disable-next-line id-length
      geometry.h = geometry.maxH;
    }
    return geometry;
  };

  const getLayoutsBySize = () => {
    const layouts = {
      lg: layout.components.map((comp) => GetGeometry(comp, 'lg')),
      md: layout.components.map((comp) => GetGeometry(comp, 'md')),
      sm: layout.components.map((comp) => GetGeometry(comp, 'sm')),
      xs: layout.components.map((comp) => GetGeometry(comp, 'xs')),
    };
    return layouts;
  };

  const CreateDashboardWidget = (componentInstance: ComponentInstance) => {
    return (
      <div
        key={componentInstance.geometry.i}
        style={
          componentInstance.style === 'Info Widget'
            ? styles.infoWidgetPanel
            : styles.widgetPanel
        }
      >
        <Widget
          componentInstance={componentInstance}
          onRemoveItem={handleRemoveItem}
          onRenameItem={handleRenameItem}
          page="Home"
          pageNumber={pageNumber.toString()}
        >
          <WidgetInstance componentInstance={componentInstance} />
        </Widget>
      </div>
    );
  };

  const CreateDashboardWidgets = () => {
    return layout.components.map((comp) => CreateDashboardWidget(comp));
  };

  return (
    <ContentLayout
      landingPageName={'home'}
      pageTitle={
        typeof layout !== 'undefined' && layout !== null ? layout.title : 'Home'
      }
      contentRight={
        <div>
          <Button
            style={styles.editIcon}
            size="large"
            shape="circle"
            title="Edit Page Title"
            icon={<EditOutlined />}
            onClick={handleEditTitle}
          />
          <Button
            style={styles.editIcon}
            size="large"
            shape="circle"
            title="Show Widget Palette"
            icon={<LayoutOutlined />}
            onClick={() => setShowWidgets(true)}
          />
        </div>
      }
    >
      <style>{`
        .react-grid-layout {
          width: 100%;
          background-color: ${token.colorBgLayout};
          margin: 0px;
          padding: 0px;
        }
      `}</style>
      <div style={styles.primaryContentPanePage}>
        <ResponsiveReactGridLayout
          draggableHandle={'.dashboardWidgetDraggable'}
          layouts={getLayoutsBySize()}
          onBreakpointChange={handleBreakpointChange}
          onWidthChange={handleWidthChange}
          onResizeStop={handleSave}
          onDragStop={handleSave}
          isDroppable
          droppingItem={
            droppingComponent
              ? {
                  // eslint-disable-next-line id-length
                  i: '__dropping-elem__',
                  // x/y are placeholders; the grid positions the dropping
                  // item from the cursor during drag-over
                  x: 0,
                  y: 0,
                  // eslint-disable-next-line id-length
                  w: droppingComponent.geometry.w,
                  // eslint-disable-next-line id-length
                  h: droppingComponent.geometry.h,
                }
              : undefined
          }
          onDrop={handleDrop}
          {...defaultProps}
        >
          {CreateDashboardWidgets()}
        </ResponsiveReactGridLayout>
        <WidgetPaletteDrawer
          visible={showWidgets}
          onClose={() => setShowWidgets(false)}
          componentPallet={componentPallet}
          onAddComponent={handleAddComponent}
          onDragStart={(comp) => {
            setDroppingComponent(comp);

            setShowWidgets(false);
          }}
          onDragEnd={() => setDroppingComponent(null)}
        />
      </div>
      {showTitleEdit && (
        <TitleEditModal
          label="Edit Page Title"
          title={layout.title}
          visible={showTitleEdit}
          onSubmit={handleTitleChange}
          onClose={() => setShowTitleEdit(false)}
        />
      )}
    </ContentLayout>
  );
};

export default Home;
